/**
 * Localized copy for /fb/pricing.
 *
 * The FB paid-ad funnel is deterministically English (or the /fb/:lang funnel
 * language). The ORGANIC landing, however, is read in lt / en / ru / es and
 * sends visitors here with ?src=landing&lang=xx - we must not drop a Lithuanian
 * parent onto an English paywall right at the moment of payment. This map holds
 * every user-facing string on the paywall keyed by language so the page can
 * render in the language the visitor was just reading.
 *
 * Kept as a local map (not translations.js/t()) on purpose: the ads funnel must
 * stay English by default regardless of any stale LanguageContext a returning
 * visitor carries, and FbPricing deliberately avoids app i18n. Icons and the
 * currency switch stay in the component; only text lives here.
 *
 * Tokens: {name} = child's name (or the localized "your child"), {child} =
 * natural "children like ___" form, {price} = formatted price+symbol.
 */

export const COPY = {
  en: {
    yourChild: 'your child',
    childLike: 'yours',
    heroTitle: '{name}’s plan is ready',
    heroSubtitle: 'Start today and help {name} build the confidence to speak up, one small daily win at a time.',
    benefits: [
      'Personalized speech & conversation practice, built around your answers',
      'Gentle real-time AI support that meets your child at their pace',
      'Guided by a speech and autism-communication framework',
      'Works on any phone or tablet. Cancel anytime.',
    ],
    families: 'families',
    rating: '4.8 average rating',
    planTitle: 'Full access',
    planNote: 'Billed today, then {price} every month. Cancel anytime in a tap.',
    review: {
      text: 'My son said his first full sentence after three weeks. I cried. Timo made him want to talk.',
      author: 'Rasa, mum of a 4-year-old',
    },
    founderTeaser: 'Hi, I’m Gintaras, 17yo, building Timo Helps',
    founderRead: 'Read our story',
    founderClose: 'Close',
    founderP1: 'My little brother went quiet every time someone new spoke to him. Teachers, other kids, anyone outside home. At home he was fine. Everywhere else, gone.',
    founderP2: 'I couldn’t find anything that actually helped, so I decided to build it, first for him, then for children like {child}.',
    founderP3: 'Today Timo is helping countless families, and we read every piece of feedback and make it better every week.',
    founderSign: 'Gintaras, founder of Timohelps.com, built with 🧡🇱🇹',
    ctaStart: 'Start now',
    legalPre: 'Secure checkout by Stripe. By continuing you agree to our',
    legalTerms: 'Terms',
    legalAnd: 'and',
    legalPrivacy: 'Privacy Policy',
    breakoutTitle: 'One quick step to check out',
    breakoutPre: 'Facebook’s in-app browser can’t open secure payments. Tap the',
    breakoutMid: 'menu at the top right, then choose',
    breakoutOpen: '“Open in browser”',
    breakoutPost: '(Safari) to finish safely.',
    breakoutRetry: 'Try opening again',
    breakoutClose: 'Close',
  },

  lt: {
    yourChild: 'jūsų vaikas',
    childLike: 'jūsų',
    heroTitle: '{name} planas paruoštas',
    heroSubtitle: 'Pradėkite šiandien ir padėkite {name} po truputį įgauti drąsos kalbėti - po vieną mažą pergalę kasdien.',
    benefits: [
      'Asmeniška kalbos ir pokalbių praktika, pritaikyta pagal jūsų atsakymus',
      'Švelni AI pagalba realiu laiku, prisitaikanti prie jūsų vaiko tempo',
      'Paremta kalbos ir autizmo komunikacijos metodika',
      'Veikia bet kuriame telefone ar planšetėje. Atšaukti galima bet kada.',
    ],
    families: 'šeimų',
    rating: '4,8 vidutinis įvertinimas',
    planTitle: 'Pilna prieiga',
    planNote: 'Nuskaičiuojama šiandien, vėliau {price} kas mėnesį. Atšaukti galite vienu paspaudimu.',
    review: {
      text: 'Mano sūnus po trijų savaičių pasakė pirmą pilną sakinį. Apsiverkiau. Timo paskatino jį kalbėti.',
      author: 'Rasa, 4 metų berniuko mama',
    },
    founderTeaser: 'Sveiki, aš Gintaras, 17 m., kuriu Timo Helps',
    founderRead: 'Skaityti mūsų istoriją',
    founderClose: 'Uždaryti',
    founderP1: 'Mano mažasis brolis nutildavo kiekvieną kartą, kai su juo prakalbdavo kas nors naujas. Mokytojai, kiti vaikai, bet kas už namų ribų. Namuose jam buvo gerai. Visur kitur - tyla.',
    founderP2: 'Neradau nieko, kas tikrai padėtų, todėl nusprendžiau tai sukurti pats - pirmiausia jam, paskui vaikams kaip {child}.',
    founderP3: 'Šiandien Timo padeda daugybei šeimų, o mes skaitome kiekvieną atsiliepimą ir kas savaitę darome jį geresnį.',
    founderSign: 'Gintaras, Timohelps.com įkūrėjas, sukurta su 🧡🇱🇹',
    ctaStart: 'Pradėti',
    legalPre: 'Saugus atsiskaitymas per Stripe. Tęsdami sutinkate su mūsų',
    legalTerms: 'Sąlygomis',
    legalAnd: 'ir',
    legalPrivacy: 'Privatumo politika',
    breakoutTitle: 'Dar vienas žingsnis atsiskaitymui',
    breakoutPre: 'Facebook vidinė naršyklė negali atidaryti saugių mokėjimų. Paspauskite',
    breakoutMid: 'meniu viršuje dešinėje ir pasirinkite',
    breakoutOpen: '„Atidaryti naršyklėje“',
    breakoutPost: '(Safari), kad saugiai baigtumėte.',
    breakoutRetry: 'Bandyti dar kartą',
    breakoutClose: 'Uždaryti',
  },

  ru: {
    yourChild: 'ваш ребёнок',
    childLike: 'ваш',
    heroTitle: 'План для {name} готов',
    heroSubtitle: 'Начните сегодня и помогите {name} обрести уверенность в общении - по одной маленькой победе каждый день.',
    benefits: [
      'Персональная практика речи и общения, построенная на ваших ответах',
      'Мягкая поддержка ИИ в реальном времени в темпе вашего ребёнка',
      'Основано на методике развития речи и общения при аутизме',
      'Работает на любом телефоне или планшете. Отмена в любой момент.',
    ],
    families: 'семей',
    rating: '4,8 средняя оценка',
    planTitle: 'Полный доступ',
    planNote: 'Списывается сегодня, затем {price} каждый месяц. Отмена в одно касание.',
    review: {
      text: 'Через три недели сын сказал первое полное предложение. Я расплакалась. Timo пробудил в нём желание говорить.',
      author: 'Раса, мама 4-летнего малыша',
    },
    founderTeaser: 'Привет, я Гинтарас, 17 лет, создаю Timo Helps',
    founderRead: 'Читать нашу историю',
    founderClose: 'Закрыть',
    founderP1: 'Мой младший брат замолкал каждый раз, когда к нему обращался кто-то новый. Учителя, другие дети, любой за пределами дома. Дома всё было хорошо. Везде ещё - тишина.',
    founderP2: 'Я не нашёл ничего, что действительно помогало бы, поэтому решил создать это сам - сначала для него, потом для таких детей, как {child}.',
    founderP3: 'Сегодня Timo помогает множеству семей, а мы читаем каждый отзыв и каждую неделю делаем его лучше.',
    founderSign: 'Гинтарас, основатель Timohelps.com, сделано с 🧡🇱🇹',
    ctaStart: 'Начать',
    legalPre: 'Безопасная оплата через Stripe. Продолжая, вы соглашаетесь с нашими',
    legalTerms: 'Условиями',
    legalAnd: 'и',
    legalPrivacy: 'Политикой конфиденциальности',
    breakoutTitle: 'Один шаг до оплаты',
    breakoutPre: 'Встроенный браузер Facebook не может открыть безопасную оплату. Нажмите',
    breakoutMid: 'меню вверху справа и выберите',
    breakoutOpen: '“Oткрыть в браузере”',
    breakoutPost: '(Safari), чтобы безопасно завершить.',
    breakoutRetry: 'Попробовать снова',
    breakoutClose: 'Закрыть',
  },

  es: {
    yourChild: 'tu hijo',
    childLike: 'el tuyo',
    heroTitle: 'El plan de {name} está listo',
    heroSubtitle: 'Empieza hoy y ayuda a {name} a ganar la confianza para hablar, con una pequeña victoria cada día.',
    benefits: [
      'Práctica de habla y conversación personalizada, según tus respuestas',
      'Apoyo suave con IA en tiempo real, al ritmo de tu hijo',
      'Guiado por un marco de habla y comunicación para el autismo',
      'Funciona en cualquier teléfono o tablet. Cancela cuando quieras.',
    ],
    families: 'familias',
    rating: '4,8 valoración media',
    planTitle: 'Acceso completo',
    planNote: 'Se cobra hoy, luego {price} cada mes. Cancela con un toque cuando quieras.',
    review: {
      text: 'Mi hijo dijo su primera frase completa después de tres semanas. Lloré. Timo hizo que quisiera hablar.',
      author: 'Rasa, mamá de un niño de 4 años',
    },
    founderTeaser: 'Hola, soy Gintaras, 17 años, creando Timo Helps',
    founderRead: 'Lee nuestra historia',
    founderClose: 'Cerrar',
    founderP1: 'Mi hermano pequeño se quedaba callado cada vez que alguien nuevo le hablaba. Profesores, otros niños, cualquiera fuera de casa. En casa estaba bien. En todas partes, en silencio.',
    founderP2: 'No encontré nada que realmente ayudara, así que decidí crearlo yo mismo, primero para él y luego para niños como {child}.',
    founderP3: 'Hoy Timo ayuda a innumerables familias, y leemos cada comentario y lo mejoramos cada semana.',
    founderSign: 'Gintaras, fundador de Timohelps.com, hecho con 🧡🇱🇹',
    ctaStart: 'Empezar ahora',
    legalPre: 'Pago seguro con Stripe. Al continuar aceptas nuestros',
    legalTerms: 'Términos',
    legalAnd: 'y la',
    legalPrivacy: 'Política de privacidad',
    breakoutTitle: 'Un paso rápido para pagar',
    breakoutPre: 'El navegador dentro de Facebook no puede abrir pagos seguros. Toca el',
    breakoutMid: 'menú arriba a la derecha y elige',
    breakoutOpen: '“Abrir en el navegador”',
    breakoutPost: '(Safari) para terminar de forma segura.',
    breakoutRetry: 'Intentar de nuevo',
    breakoutClose: 'Cerrar',
  },

  fr: {
    yourChild: 'votre enfant',
    childLike: 'le vôtre',
    heroTitle: 'Le plan de {name} est prêt',
    heroSubtitle: 'Commencez aujourd’hui et aidez {name} à gagner la confiance de s’exprimer, une petite victoire à la fois.',
    benefits: [
      'Pratique personnalisée du langage et de la conversation, selon vos réponses',
      'Un soutien doux par IA en temps réel, au rythme de votre enfant',
      'Guidé par un cadre de langage et de communication pour l’autisme',
      'Fonctionne sur tout téléphone ou tablette. Annulez quand vous voulez.',
    ],
    families: 'familles',
    rating: '4,8 note moyenne',
    planTitle: 'Accès complet',
    planNote: 'Débité aujourd’hui, puis {price} chaque mois. Annulez en un geste.',
    review: {
      text: 'Mon fils a dit sa première phrase complète après trois semaines. J’ai pleuré. Timo lui a donné envie de parler.',
      author: 'Rasa, maman d’un enfant de 4 ans',
    },
    founderTeaser: 'Bonjour, je suis Gintaras, 17 ans, je crée Timo Helps',
    founderRead: 'Lire notre histoire',
    founderClose: 'Fermer',
    founderP1: 'Mon petit frère se taisait chaque fois qu’une nouvelle personne lui parlait. Les enseignants, les autres enfants, tout le monde en dehors de la maison. À la maison, tout allait bien. Partout ailleurs, le silence.',
    founderP2: 'Je n’ai rien trouvé qui aide vraiment, alors j’ai décidé de le créer moi-même, d’abord pour lui, puis pour des enfants comme {child}.',
    founderP3: 'Aujourd’hui, Timo aide d’innombrables familles, et nous lisons chaque retour pour l’améliorer chaque semaine.',
    founderSign: 'Gintaras, fondateur de Timohelps.com, fait avec 🧡🇱🇹',
    ctaStart: 'Commencer',
    legalPre: 'Paiement sécurisé par Stripe. En continuant, vous acceptez nos',
    legalTerms: 'Conditions',
    legalAnd: 'et la',
    legalPrivacy: 'Politique de confidentialité',
    breakoutTitle: 'Une étape rapide pour payer',
    breakoutPre: 'Le navigateur intégré de Facebook ne peut pas ouvrir les paiements sécurisés. Touchez le',
    breakoutMid: 'menu en haut à droite, puis choisissez',
    breakoutOpen: '“Ouvrir dans le navigateur”',
    breakoutPost: '(Safari) pour terminer en toute sécurité.',
    breakoutRetry: 'Réessayer',
    breakoutClose: 'Fermer',
  },
};

// Fill {name} / {child} / {price} tokens.
export function fill(str, vars = {}) {
  if (!str) return str;
  return str
    .replace(/\{name\}/g, vars.name ?? '')
    .replace(/\{child\}/g, vars.child ?? '')
    .replace(/\{price\}/g, vars.price ?? '');
}
